import { useState, useCallback, useEffect } from 'react'
import { Reorder, AnimatePresence, motion } from 'framer-motion'
import {
  FileJson,
  Image,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  GripVertical,
  FileText,
  Copy,
  Check,
  Sparkles,
  BookOpen,
  User,
  MessageSquare,
  MapPin,
  BrainCircuit,
  AlertCircle,
  Loader2,
  Edit3,
  Save,
  X,
  Wand2,
  Plus,
  Trash2,
  Users2,
} from 'lucide-react'
import FileUpload from '../components/FileUpload'
import ToggleSwitch from '../components/ToggleSwitch'
import CodeBlock from '../components/CodeBlock'
import { useToast } from '../components/Toast'
import { extractCardFromPng, embedCardIntoPng } from '../utils/png'
import {
  parseCharacterCard,
  generateCardOutput,
  replaceVariables,
} from '../utils/cardParser'
import type { ConditionalEntry, ExportedRegexScript } from '../utils/cardParser'
import { WORLD_INFO_POSITION_LABELS } from '../types'
import type { WorldInfoPositionValue } from '../types'
import type { ParsedCard, WorldBookEntry } from '../types'

/* ------------------------------------------------------------------ */
// 可折叠区域组件

interface CollapsibleSectionProps {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
  defaultOpen?: boolean
  onEdit?: () => void
  isEditing?: boolean
  editContent?: string
  onSaveEdit?: (content: string) => void
  onCancelEdit?: () => void
}

function CollapsibleSection({
  title,
  icon,
  children,
  defaultOpen = false,
  onEdit,
  isEditing,
  editContent,
  onSaveEdit,
  onCancelEdit,
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const [editValue, setEditValue] = useState(editContent || '')

  useEffect(() => {
    setEditValue(editContent || '')
  }, [editContent])

  return (
    <div className="ui-card overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 hover:bg-raised/40 transition-colors">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 text-fg flex-1 text-left min-w-0"
        >
          {icon}
          <span className="font-medium truncate">{title}</span>
          {isOpen ? (
            <ChevronUp size={18} className="text-faint shrink-0" />
          ) : (
            <ChevronDown size={18} className="text-faint shrink-0" />
          )}
        </button>
        {onEdit && !isEditing && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onEdit()
            }}
            className="text-faint hover:text-accent transition-colors p-1 shrink-0"
            title="编辑"
          >
            <Edit3 size={16} />
          </button>
        )}
      </div>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4">
              {isEditing ? (
                <div className="space-y-3">
                  <textarea
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    rows={8}
                    className="ui-textarea leading-relaxed min-h-[160px]"
                    style={{ touchAction: 'pan-y' }}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => onSaveEdit?.(editValue)}
                      className="ui-btn ui-btn-primary px-3 py-1.5 rounded-md"
                    >
                      <Save size={14} />
                      保存
                    </button>
                    <button
                      onClick={onCancelEdit}
                      className="ui-btn ui-btn-ghost px-3 py-1.5 rounded-md"
                    >
                      <X size={14} />
                      取消
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-muted whitespace-pre-wrap leading-relaxed">
                  {children}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

interface CollapsibleOutputProps {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
  defaultOpen?: boolean
  action?: React.ReactNode
}

function CollapsibleOutput({ title, icon, children, defaultOpen = true, action }: CollapsibleOutputProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 text-left min-w-0"
        >
          <h3 className="text-base font-semibold text-fg flex items-center gap-2 tracking-tight">
            {icon}
            <span className="truncate">{title}</span>
          </h3>
          {isOpen ? (
            <ChevronUp size={18} className="text-faint shrink-0" />
          ) : (
            <ChevronDown size={18} className="text-faint shrink-0" />
          )}
        </button>
        {action}
      </div>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/* ------------------------------------------------------------------ */
// 主组件

export default function CardParser() {
  const { showToast } = useToast()
  const [card, setCard] = useState<ParsedCard | null>(null)
  const [entries, setEntries] = useState<WorldBookEntry[]>([])
  const [originalPngBuffer, setOriginalPngBuffer] = useState<ArrayBuffer | null>(null)
  const [originalFileName, setOriginalFileName] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [systemPrompt, setSystemPrompt] = useState('')
  const [conditionalEntries, setConditionalEntries] = useState<ConditionalEntry[]>([])
  const [regexScripts, setRegexScripts] = useState<ExportedRegexScript[]>([])
  const [initialVariables, setInitialVariables] = useState<Record<string, string>>({})
  const [alternateGreetings, setAlternateGreetings] = useState<string[]>([])
  const [groupGreetings, setGroupGreetings] = useState<string[]>([])
  const [greetingIndex, setGreetingIndex] = useState(0)
  const [copied, setCopied] = useState(false)

  // 折叠状态
  const [altGreetingsOpen, setAltGreetingsOpen] = useState(false)
  const [groupGreetingsOpen, setGroupGreetingsOpen] = useState(false)
  const [worldBookOpen, setWorldBookOpen] = useState(false)

  // 编辑状态
  const [editingField, setEditingField] = useState<string | null>(null)
  const [editingAltIndex, setEditingAltIndex] = useState<number | null>(null)
  const [editingGroupIndex, setEditingGroupIndex] = useState<number | null>(null)

  // 实时生成 Markdown
  useEffect(() => {
    if (card) {
      const output = generateCardOutput(card, entries)
      setSystemPrompt(output.systemPrompt)
      setConditionalEntries(output.conditionalEntries)
      setRegexScripts(output.regexScripts)
      setInitialVariables(output.initialVariables)
      setAlternateGreetings(output.alternateGreetings)
      setGroupGreetings(output.groupGreetings)
      setGreetingIndex(0)
    }
  }, [card, entries])

  const handleFileSelect = useCallback(
    async (file: File) => {
      setIsLoading(true)
      setCard(null)
      setEntries([])
      setOriginalPngBuffer(null)
      setOriginalFileName('')
      setEditingField(null)

      try {
        const extension = file.name.split('.').pop()?.toLowerCase()

        let jsonData: any

        if (extension === 'json') {
          const text = await file.text()
          jsonData = JSON.parse(text)
        } else if (extension === 'png') {
          const arrayBuffer = await file.arrayBuffer()
          jsonData = await extractCardFromPng(arrayBuffer)
          setOriginalPngBuffer(arrayBuffer)
          setOriginalFileName(file.name)
        } else {
          throw new Error('不支持的文件格式，请上传 .json 或 .png 文件')
        }

        const parsed = parseCharacterCard(jsonData)
        setCard(parsed)
        setEntries(parsed.entries)
        showToast('角色卡解析成功', 'success')
      } catch (error) {
        const message =
          error instanceof Error ? error.message : '解析失败'
        showToast(message, 'error')
      } finally {
        setIsLoading(false)
      }
    },
    [showToast]
  )

  // 世界书条目操作
  const handleToggleEntry = useCallback((index: number) => {
    setEntries((prev) =>
      prev.map((entry, i) =>
        i === index ? { ...entry, enabled: !entry.enabled } : entry
      )
    )
  }, [])

  const handleEntryContentChange = useCallback(
    (index: number, newContent: string) => {
      setEntries((prev) =>
        prev.map((entry, i) =>
          i === index ? { ...entry, content: newContent } : entry
        )
      )
    },
    []
  )

  // 字段编辑
  const startEdit = useCallback((field: string, _value: string) => {
    setEditingField(field)
  }, [])

  const saveEdit = useCallback((field: string, value: string) => {
    setCard((prev) => {
      if (!prev) return prev
      return { ...prev, [field]: value }
    })
    setEditingField(null)
  }, [])

  const cancelEdit = useCallback(() => {
    setEditingField(null)
  }, [])

  // 备选开场白操作
  const handleAddAltGreeting = useCallback(() => {
    setCard((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        alternate_greetings: [...prev.alternate_greetings, ''],
      }
    })
  }, [])

  const handleRemoveAltGreeting = useCallback((index: number) => {
    setCard((prev) => {
      if (!prev) return prev
      const updated = prev.alternate_greetings.filter((_, i) => i !== index)
      return { ...prev, alternate_greetings: updated }
    })
    setEditingAltIndex(null)
  }, [])

  // 群组开场白操作
  const handleAddGroupGreeting = useCallback(() => {
    setCard((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        group_only_greetings: [...(prev.group_only_greetings || []), ''],
      }
    })
  }, [])

  const handleRemoveGroupGreeting = useCallback((index: number) => {
    setCard((prev) => {
      if (!prev) return prev
      const updated = (prev.group_only_greetings || []).filter((_, i) => i !== index)
      return { ...prev, group_only_greetings: updated }
    })
    setEditingGroupIndex(null)
  }, [])

  // PNG 导出
  const handleExportPng = useCallback(async () => {
    if (!card || !originalPngBuffer) return

    setIsLoading(true)
    try {
      const exportData: any = {
        name: card.name,
        description: card.description,
        personality: card.personality,
        scenario: card.scenario,
        first_mes: card.first_mes,
        mes_example: card.mes_example,
        creatorcomment: card.creatorcomment,
        tags: card.tags,
        creator: card.creator,
        character_version: card.character_version,
        alternate_greetings: card.alternate_greetings,
        group_only_greetings: card.group_only_greetings,
        system_prompt: card.system_prompt,
        post_history_instructions: card.post_history_instructions,
        extensions: card.extensions,
        character_book: card.character_book
          ? {
              ...card.character_book,
              entries,
            }
          : undefined,
      }

      const blob = await embedCardIntoPng(originalPngBuffer, exportData)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = originalFileName.replace('.png', '_modified.png')
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      showToast('PNG 导出成功', 'success')
    } catch (error) {
      const message =
        error instanceof Error ? error.message : '导出失败'
      showToast(message, 'error')
    } finally {
      setIsLoading(false)
    }
  }, [card, entries, originalPngBuffer, originalFileName, showToast])

  // 构建角色卡（从文本+图片生成）
  const [buildMode, setBuildMode] = useState(false)
  const [buildText, setBuildText] = useState('')
  const [buildImage, setBuildImage] = useState<ArrayBuffer | null>(null)
  const [buildImageName, setBuildImageName] = useState('')

  const handleBuildImageSelect = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      showToast('请上传图片文件', 'error')
      return
    }
    const buffer = await file.arrayBuffer()
    setBuildImage(buffer)
    setBuildImageName(file.name)
    showToast('图片已上传', 'success')
  }, [showToast])

  const handleBuildCard = useCallback(async () => {
    if (!buildText.trim()) {
      showToast('请输入角色卡文本内容', 'error')
      return
    }

    try {
      let cardData: any
      try {
        cardData = JSON.parse(buildText)
      } catch {
        cardData = {
          name: '未命名角色',
          description: buildText,
          first_mes: '',
          scenario: '',
          personality: '',
          spec: 'chara_card_v3',
          spec_version: '3.0',
          data: {
            name: '未命名角色',
            description: buildText,
            first_mes: '',
            scenario: '',
            personality: '',
          }
        }
      }

      if (buildImage) {
        const blob = await embedCardIntoPng(buildImage, cardData)
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = buildImageName.replace(/\.[^.]+$/, '_card.png')
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        showToast('角色卡 PNG 已生成', 'success')
      } else {
        const blob = new Blob([JSON.stringify(cardData, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${cardData.name || 'character'}.json`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        showToast('角色卡 JSON 已导出', 'success')
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '生成失败'
      showToast(message, 'error')
    }
  }, [buildText, buildImage, buildImageName, showToast])

  return (
    <div className="space-y-6">
      {/* 模式切换 */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setBuildMode(false)}
          aria-pressed={!buildMode}
          className={`ui-btn ${!buildMode ? 'ui-btn-primary' : 'ui-btn-ghost'}`}
        >
          <FileJson size={18} />
          解析角色卡
        </button>
        <button
          onClick={() => setBuildMode(true)}
          aria-pressed={buildMode}
          className={`ui-btn ${buildMode ? 'ui-btn-primary' : 'ui-btn-ghost'}`}
        >
          <Wand2 size={18} />
          构建角色卡
        </button>
      </div>

      {buildMode ? (
        /* ==================== 构建模式 ==================== */
        <div className="space-y-6">
          <div className="ui-card p-6 space-y-4">
            <h2 className="text-lg font-semibold text-fg flex items-center gap-2 tracking-tight">
              <Wand2 size={20} className="text-accent" />
              构建角色卡
            </h2>
            <p className="text-sm text-muted">
              粘贴 AI 生成的角色卡 JSON 或文本内容，上传一张图片作为角色卡封面，即可生成 PNG 角色卡。
            </p>

            {/* 文本输入 */}
            <div>
              <label className="block text-sm font-medium text-muted mb-2">
                角色卡内容（JSON 或纯文本）
              </label>
              <textarea
                value={buildText}
                onChange={(e) => setBuildText(e.target.value)}
                rows={12}
                className="ui-textarea leading-relaxed"
                placeholder="粘贴角色卡 JSON 或文本内容..."
              />
            </div>

            {/* 图片上传 */}
            <div>
              <label className="block text-sm font-medium text-muted mb-2">
                角色卡封面图片（可选）
              </label>
              <FileUpload
                accept="image/*"
                onFileSelect={handleBuildImageSelect}
                label={buildImageName || "上传图片（PNG/JPG）"}
              />
            </div>

            {/* 生成按钮 */}
            <button
              onClick={handleBuildCard}
              className="ui-btn ui-btn-primary px-5 py-2.5 rounded-md"
            >
              <Sparkles size={18} />
              生成角色卡
            </button>
          </div>
        </div>
      ) : (
        /* ==================== 解析模式 ==================== */
        <>
          {/* Header */}
          <div>
            <h1 className="text-2xl font-bold text-fg mb-2 flex items-center gap-3 tracking-tight">
              <Sparkles className="text-accent" size={26} />
              角色卡解析器
            </h1>
            <p className="text-muted">
              解析 SillyTavern 角色卡（JSON / PNG），编辑世界书条目并导出 Markdown
            </p>
          </div>

          {/* File Upload */}
          <div className="ui-card p-6">
            <FileUpload
              accept=".json,.png"
              onFileSelect={handleFileSelect}
              label="上传角色卡文件"
            />
          </div>

          {/* Loading */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={28} className="text-accent animate-spin" />
              <span className="ml-3 text-muted">正在解析...</span>
            </div>
          )}

          {/* Character Info */}
          {card && !isLoading && (
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,440px)] gap-6 items-start">
              {/* Left column — edit (header + fields + greetings + worldbook + actions) */}
              <div className="space-y-6 order-2 lg:order-1">
              {/* Character Header */}
              <div className="ui-raised rounded-lg p-5">
                <h2 className="text-xl font-bold text-fg mb-4 flex items-center gap-2 tracking-tight">
                  <User size={20} className="text-accent" />
                  {replaceVariables(card.name) || '未命名角色'}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {card.scenario && (
                    <div className="flex items-start gap-2 text-muted">
                      <MapPin size={16} className="text-faint mt-1 shrink-0" />
                      <div>
                        <span className="text-xs text-faint uppercase tracking-wider">
                          场景
                        </span>
                        <p className="text-sm">{replaceVariables(card.scenario)}</p>
                      </div>
                    </div>
                  )}
                  {card.personality && (
                    <div className="flex items-start gap-2 text-muted">
                      <BrainCircuit size={16} className="text-faint mt-1 shrink-0" />
                      <div>
                        <span className="text-xs text-faint uppercase tracking-wider">
                          性格
                        </span>
                        <p className="text-sm">{replaceVariables(card.personality)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Editable Collapsible Sections */}
              <div className="space-y-3">
                {card.system_prompt && (
                  <CollapsibleSection
                    title="System Prompt (角色卡)"
                    icon={<FileText size={18} className="text-accent" />}
                    defaultOpen={true}
                    onEdit={() => startEdit('system_prompt', card.system_prompt)}
                    isEditing={editingField === 'system_prompt'}
                    editContent={card.system_prompt}
                    onSaveEdit={(value) => saveEdit('system_prompt', value)}
                    onCancelEdit={cancelEdit}
                  >
                    {replaceVariables(card.system_prompt)}
                  </CollapsibleSection>
                )}
                {card.description && (
                  <CollapsibleSection
                    title="角色描述"
                    icon={<FileText size={18} className="text-faint" />}
                    onEdit={() => startEdit('description', card.description)}
                    isEditing={editingField === 'description'}
                    editContent={card.description}
                    onSaveEdit={(value) => saveEdit('description', value)}
                    onCancelEdit={cancelEdit}
                  >
                    {replaceVariables(card.description)}
                  </CollapsibleSection>
                )}
                {card.first_mes && (
                  <CollapsibleSection
                    title="第一条消息"
                    icon={<MessageSquare size={18} className="text-faint" />}
                    onEdit={() => startEdit('first_mes', card.first_mes)}
                    isEditing={editingField === 'first_mes'}
                    editContent={card.first_mes}
                    onSaveEdit={(value) => saveEdit('first_mes', value)}
                    onCancelEdit={cancelEdit}
                  >
                    {replaceVariables(card.first_mes)}
                  </CollapsibleSection>
                )}
                {card.mes_example && (
                  <CollapsibleSection
                    title="示例对话"
                    icon={<MessageSquare size={18} className="text-faint" />}
                    onEdit={() => startEdit('mes_example', card.mes_example)}
                    isEditing={editingField === 'mes_example'}
                    editContent={card.mes_example}
                    onSaveEdit={(value) => saveEdit('mes_example', value)}
                    onCancelEdit={cancelEdit}
                  >
                    {replaceVariables(card.mes_example)}
                  </CollapsibleSection>
                )}
                {card.scenario && (
                  <CollapsibleSection
                    title="场景设定"
                    icon={<MapPin size={18} className="text-faint" />}
                    onEdit={() => startEdit('scenario', card.scenario)}
                    isEditing={editingField === 'scenario'}
                    editContent={card.scenario}
                    onSaveEdit={(value) => saveEdit('scenario', value)}
                    onCancelEdit={cancelEdit}
                  >
                    {replaceVariables(card.scenario)}
                  </CollapsibleSection>
                )}
                {card.personality && (
                  <CollapsibleSection
                    title="性格设定"
                    icon={<BrainCircuit size={18} className="text-faint" />}
                    onEdit={() => startEdit('personality', card.personality)}
                    isEditing={editingField === 'personality'}
                    editContent={card.personality}
                    onSaveEdit={(value) => saveEdit('personality', value)}
                    onCancelEdit={cancelEdit}
                  >
                    {replaceVariables(card.personality)}
                  </CollapsibleSection>
                )}
                {card.post_history_instructions && (
                  <CollapsibleSection
                    title="Post-History Instructions"
                    icon={<FileText size={18} className="text-[#e0625f]" />}
                    onEdit={() => startEdit('post_history_instructions', card.post_history_instructions)}
                    isEditing={editingField === 'post_history_instructions'}
                    editContent={card.post_history_instructions}
                    onSaveEdit={(value) => saveEdit('post_history_instructions', value)}
                    onCancelEdit={cancelEdit}
                  >
                    {replaceVariables(card.post_history_instructions)}
                  </CollapsibleSection>
                )}
              </div>

              {/* Alternate Greetings */}
              <div className="ui-card overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3">
                  <button
                    onClick={() => setAltGreetingsOpen(!altGreetingsOpen)}
                    className="flex items-center gap-2 text-fg flex-1 text-left min-w-0"
                  >
                    <MessageSquare size={18} className="text-faint" />
                    <span className="font-medium">备选开场白</span>
                    <span className="text-xs text-faint">
                      ({(card.alternate_greetings || []).length})
                    </span>
                    {altGreetingsOpen ? (
                      <ChevronUp size={18} className="text-faint shrink-0" />
                    ) : (
                      <ChevronDown size={18} className="text-faint shrink-0" />
                    )}
                  </button>
                  <button
                    onClick={handleAddAltGreeting}
                    className="text-faint hover:text-accent transition-colors p-1 shrink-0"
                    title="添加开场白"
                  >
                    <Plus size={18} />
                  </button>
                </div>
                <AnimatePresence initial={false}>
                  {altGreetingsOpen && (card.alternate_greetings || []).length > 0 && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-2 space-y-2">
                        {(card.alternate_greetings || []).map((greeting, index) => (
                          <div
                            key={index}
                            className="border border-stroke rounded-md bg-bg overflow-hidden"
                          >
                            <div className="flex items-center justify-between px-3 py-2">
                              <span className="text-sm text-muted">
                                开场白 {index + 1}
                              </span>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => setEditingAltIndex(index)}
                                  className="text-faint hover:text-accent transition-colors p-1"
                                  title="编辑"
                                >
                                  <Edit3 size={14} />
                                </button>
                                <button
                                  onClick={() => handleRemoveAltGreeting(index)}
                                  className="text-faint hover:text-[#e0625f] transition-colors p-1"
                                  title="删除"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                            {editingAltIndex === index ? (
                              <div className="px-3 pb-3 space-y-2">
                                <textarea
                                  value={greeting}
                                  onChange={(e) => {
                                    setCard((prev) => {
                                      if (!prev) return prev
                                      const updated = [
                                        ...(prev.alternate_greetings || []),
                                      ]
                                      updated[index] = e.target.value
                                      return {
                                        ...prev,
                                        alternate_greetings: updated,
                                      }
                                    })
                                  }}
                                  rows={4}
                                  className="ui-textarea leading-relaxed"
                                />
                                <button
                                  onClick={() => setEditingAltIndex(null)}
                                  className="ui-btn ui-btn-primary px-3 py-1 rounded-md text-sm"
                                >
                                  <Save size={12} />
                                  完成
                                </button>
                              </div>
                            ) : (
                              <div className="px-3 pb-3">
                                <p className="text-muted whitespace-pre-wrap leading-relaxed text-sm">
                                  {replaceVariables(greeting) || (
                                    <span className="text-faint italic">
                                      空开场白
                                    </span>
                                  )}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Group Only Greetings */}
              <div className="ui-card overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3">
                  <button
                    onClick={() => setGroupGreetingsOpen(!groupGreetingsOpen)}
                    className="flex items-center gap-2 text-fg flex-1 text-left min-w-0"
                  >
                    <Users2 size={18} className="text-faint" />
                    <span className="font-medium">群组开场白</span>
                    <span className="text-xs text-faint">
                      ({(card.group_only_greetings || []).length})
                    </span>
                    {groupGreetingsOpen ? (
                      <ChevronUp size={18} className="text-faint shrink-0" />
                    ) : (
                      <ChevronDown size={18} className="text-faint shrink-0" />
                    )}
                  </button>
                  <button
                    onClick={handleAddGroupGreeting}
                    className="text-faint hover:text-accent transition-colors p-1 shrink-0"
                    title="添加群组开场白"
                  >
                    <Plus size={18} />
                  </button>
                </div>
                <AnimatePresence initial={false}>
                  {groupGreetingsOpen && (card.group_only_greetings || []).length > 0 && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-2 space-y-2">
                        {(card.group_only_greetings || []).map((greeting, index) => (
                          <div
                            key={index}
                            className="border border-stroke rounded-md bg-bg overflow-hidden"
                          >
                            <div className="flex items-center justify-between px-3 py-2">
                              <span className="text-sm text-muted">
                                开场白 {index + 1}
                              </span>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => setEditingGroupIndex(index)}
                                  className="text-faint hover:text-accent transition-colors p-1"
                                  title="编辑"
                                >
                                  <Edit3 size={14} />
                                </button>
                                <button
                                  onClick={() => handleRemoveGroupGreeting(index)}
                                  className="text-faint hover:text-[#e0625f] transition-colors p-1"
                                  title="删除"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                            {editingGroupIndex === index ? (
                              <div className="px-3 pb-3 space-y-2">
                                <textarea
                                  value={greeting}
                                  onChange={(e) => {
                                    setCard((prev) => {
                                      if (!prev) return prev
                                      const updated = [
                                        ...(prev.group_only_greetings || []),
                                      ]
                                      updated[index] = e.target.value
                                      return {
                                        ...prev,
                                        group_only_greetings: updated,
                                      }
                                    })
                                  }}
                                  rows={4}
                                  className="ui-textarea leading-relaxed"
                                />
                                <button
                                  onClick={() => setEditingGroupIndex(null)}
                                  className="ui-btn ui-btn-primary px-3 py-1 rounded-md text-sm"
                                >
                                  <Save size={12} />
                                  完成
                                </button>
                              </div>
                            ) : (
                              <div className="px-3 pb-3">
                                <p className="text-muted whitespace-pre-wrap leading-relaxed text-sm">
                                  {replaceVariables(greeting) || (
                                    <span className="text-faint italic">
                                      空开场白
                                    </span>
                                  )}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* World Book Entries */}
              {entries.length > 0 && (
                <div className="ui-card overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3">
                    <button
                      onClick={() => setWorldBookOpen(!worldBookOpen)}
                      className="flex items-center gap-2 text-fg flex-1 text-left min-w-0"
                    >
                      <BookOpen size={18} className="text-accent" />
                      <span className="font-medium">世界书条目</span>
                      <span className="text-xs text-faint">
                        ({entries.filter((e) => e.enabled).length} / {entries.length})
                      </span>
                      {worldBookOpen ? (
                        <ChevronUp size={18} className="text-faint shrink-0" />
                      ) : (
                        <ChevronDown size={18} className="text-faint shrink-0" />
                      )}
                    </button>
                  </div>
                  <AnimatePresence initial={false}>
                    {worldBookOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-4">
                          <Reorder.Group
                            axis="y"
                            values={entries}
                            onReorder={setEntries}
                            className="space-y-2"
                          >
                            {entries.map((entry, index) => (
                              <Reorder.Item
                                key={entry.id}
                                value={entry}
                                className="ui-card overflow-hidden"
                                style={{ touchAction: 'pan-y' }}
                              >
                                <WorldBookEntryItem
                                  entry={entry}
                                  index={index}
                                  onToggle={() => handleToggleEntry(index)}
                                  onContentChange={(content) =>
                                    handleEntryContentChange(index, content)
                                  }
                                />
                              </Reorder.Item>
                            ))}
                          </Reorder.Group>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap items-center gap-3">
                {originalPngBuffer && (
                  <button
                    onClick={handleExportPng}
                    disabled={isLoading}
                    className="ui-btn px-5 py-2.5 rounded-md disabled:opacity-50 disabled:pointer-events-none"
                  >
                    <Image size={18} />
                    导出 PNG
                  </button>
                )}
              </div>
              </div>{/* /left column */}

              {/* Right column — live output preview (sticky on desktop) */}
              <div className="space-y-5 order-1 lg:order-2 lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto lg:pr-1">

              {/* System Prompt Output */}
              {systemPrompt && (
                <CollapsibleOutput
                  title="系统提示（固定内容）"
                  icon={<Sparkles size={20} className="text-accent" />}
                  defaultOpen={true}
                  action={
                    <button
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(systemPrompt)
                          setCopied(true)
                          setTimeout(() => setCopied(false), 2000)
                          showToast('已复制到剪贴板', 'success')
                        } catch { showToast('复制失败', 'error') }
                      }}
                      className="ui-btn px-3 py-1.5 rounded-md text-sm"
                    >
                      {copied ? (
                        <><Check size={14} className="text-accent" /> 已复制</>
                      ) : (
                        <><Copy size={14} /> 复制</>
                      )}
                    </button>
                  }
                >
                  <p className="text-xs text-faint mb-3">
                    包含角色卡描述、性格、场景、constant 世界书条目等固定内容。可直接用作 system prompt。
                  </p>
                  <CodeBlock content={systemPrompt} language="text" />
                </CollapsibleOutput>
              )}

              {/* Initial Variables */}
              {Object.keys(initialVariables).length > 0 && (
                <CollapsibleOutput
                  title={`初始化变量 (${Object.keys(initialVariables).length})`}
                  icon={<Sparkles size={20} className="text-[#5ec27a]" />}
                  defaultOpen={true}
                  action={
                    <button
                      onClick={async () => {
                        try {
                          const json = JSON.stringify(initialVariables, null, 2)
                          await navigator.clipboard.writeText(json)
                          showToast('已复制变量 JSON', 'success')
                        } catch { showToast('复制失败', 'error') }
                      }}
                      className="ui-btn px-3 py-1.5 rounded-md text-sm"
                    >
                      <Copy size={14} /> 复制 JSON
                    </button>
                  }
                >
                  <p className="text-xs text-faint mb-3">
                    从角色卡和 constant 世界书中提取的变量初始化。导入 SmartChat 时应初始化这些变量。
                  </p>
                  <div className="p-3 rounded-md bg-bg border border-stroke">
                    <CodeBlock content={JSON.stringify(initialVariables, null, 2)} language="json" showLineNumbers={false} />
                  </div>
                </CollapsibleOutput>
              )}

              {/* Greetings Output */}
              {(() => {
                const allGreetings = [
                  ...(systemPrompt && card?.first_mes ? [{ label: '开场白', text: replaceVariables(card.first_mes, card.name || 'Character') }] : []),
                  ...alternateGreetings.map((g, i) => ({ label: `备选开场白 ${i + 1}`, text: g })),
                  ...groupGreetings.map((g, i) => ({ label: `群组开场白 ${i + 1}`, text: g })),
                ].filter((g) => g.text)
                if (allGreetings.length === 0) return null
                const idx = Math.min(greetingIndex, allGreetings.length - 1)
                const current = allGreetings[idx]
                return (
                  <CollapsibleOutput
                    title={`开场白 (${allGreetings.length})`}
                    icon={<MessageSquare size={20} className="text-[#5ec27a]" />}
                    defaultOpen={false}
                    action={
                      <button
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(current.text)
                            showToast('已复制到剪贴板', 'success')
                          } catch { showToast('复制失败', 'error') }
                        }}
                        className="ui-btn px-3 py-1.5 rounded-md text-sm"
                      >
                        <Copy size={14} /> 复制
                      </button>
                    }
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-muted">{current.label}</span>
                      {allGreetings.length > 1 && (
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => setGreetingIndex(Math.max(0, idx - 1))}
                            disabled={idx === 0}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-stroke-2 bg-bg text-muted hover:text-fg hover:border-faint disabled:opacity-30 disabled:pointer-events-none transition-colors"
                          >
                            <ChevronLeft size={14} />
                          </button>
                          <span className="text-xs text-faint tabular-nums min-w-[3em] text-center">
                            {idx + 1}/{allGreetings.length}
                          </span>
                          <button
                            onClick={() => setGreetingIndex(Math.min(allGreetings.length - 1, idx + 1))}
                            disabled={idx === allGreetings.length - 1}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-stroke-2 bg-bg text-muted hover:text-fg hover:border-faint disabled:opacity-30 disabled:pointer-events-none transition-colors"
                          >
                            <ChevronRight size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                    <CodeBlock content={current.text} language="text" />
                  </CollapsibleOutput>
                )
              })()}

              {/* Conditional Entries Output */}
              {conditionalEntries.length > 0 && (
                <CollapsibleOutput
                  title={`条件世界书条目 (${conditionalEntries.length})`}
                  icon={<BookOpen size={20} className="text-[#c47d12]" />}
                  defaultOpen={true}
                  action={
                    <button
                      onClick={async () => {
                        try {
                          const json = JSON.stringify(conditionalEntries, null, 2)
                          await navigator.clipboard.writeText(json)
                          showToast('已复制 JSON', 'success')
                        } catch { showToast('复制失败', 'error') }
                      }}
                      className="ui-btn px-3 py-1.5 rounded-md text-sm"
                    >
                      <Copy size={14} /> 复制 JSON
                    </button>
                  }
                >
                  <p className="text-xs text-faint mb-3">
                    这些条目需要关键词触发才激活。触发时会注入文本并更新变量。
                  </p>
                  <div className="space-y-2">
                    {conditionalEntries.map((entry, i) => (
                      <div key={i} className="p-3 rounded-md bg-bg border border-stroke">
                        <div className="text-xs text-faint mb-2">
                          触发词: {entry.keys.join(', ')}
                          {Object.keys(entry.setVars).length > 0 && (
                            <span className="ml-2 text-[#5ec27a]/80">
                              | 变量更新: {Object.entries(entry.setVars).map(([k, v]) => `${k}=${v}`).join(', ')}
                            </span>
                          )}
                        </div>
                        {entry.content && (
                          <div className="text-sm text-muted whitespace-pre-wrap leading-relaxed max-h-32 overflow-y-auto">
                            {entry.content}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CollapsibleOutput>
              )}

              {/* Regex Scripts Output */}
              {regexScripts.length > 0 && (
                <CollapsibleOutput
                  title={`正则脚本 (${regexScripts.length})`}
                  icon={<Sparkles size={20} className="text-accent" />}
                  defaultOpen={true}
                  action={
                    <button
                      onClick={async () => {
                        try {
                          const json = JSON.stringify(regexScripts, null, 2)
                          await navigator.clipboard.writeText(json)
                          showToast('已复制 JSON', 'success')
                        } catch { showToast('复制失败', 'error') }
                      }}
                      className="ui-btn px-3 py-1.5 rounded-md text-sm"
                    >
                      <Copy size={14} /> 复制 JSON
                    </button>
                  }
                >
                  <p className="text-xs text-faint mb-3">
                    JSON 格式: [&#123;&quot;name&quot;, &quot;find&quot;, &quot;replace&quot;, &quot;target&quot;&#125;]。target: display=仅显示, prompt=仅发送给AI, both=两者, permanent=直接修改消息
                  </p>
                  <div className="space-y-2">
                    {regexScripts.map((script, i) => {
                      const targetLabel: Record<string, { text: string; cls: string }> = {
                        display: { text: '仅显示', cls: 'bg-accent-soft text-accent/90' },
                        prompt: { text: '仅AI', cls: 'bg-[#f5a623]/15 text-[#ffb340]/90' },
                        both: { text: '显示+AI', cls: 'bg-[#5ec27a]/15 text-[#5ec27a]/90' },
                        permanent: { text: '永久', cls: 'bg-[#e0625f]/15 text-[#e0625f]/90' },
                      }
                      const t = targetLabel[script.target] || targetLabel.permanent
                      return (
                        <div key={i} className="p-3 rounded-md bg-bg border border-stroke">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm font-medium text-fg">{script.name || '(未命名)'}</span>
                            <span className={`px-1.5 py-0.5 rounded text-xs ${t.cls}`}>{t.text}</span>
                          </div>
                          <div className="text-xs text-faint space-y-1">
                            <div><span className="text-faint">匹配:</span> <code className="text-accent/80 break-all">{script.find}</code></div>
                            <div><span className="text-faint">替换:</span> <code className="text-accent/80 break-all">{script.replace || '(删除匹配)'}</code></div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </CollapsibleOutput>
              )}

              </div>{/* /right column */}
            </div>
          )}

          {/* Empty State */}
          {!card && !isLoading && !buildMode && (
            <div className="flex flex-col items-center justify-center py-16 text-faint">
              <div className="w-14 h-14 rounded-md bg-surface border border-stroke flex items-center justify-center mb-4">
                <FileJson size={32} />
              </div>
              <p className="text-lg font-medium text-fg mb-1">
                尚未上传角色卡
              </p>
              <p className="text-sm">上传 .json 或 .png 文件开始解析</p>
            </div>
          )}
        </>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
// 子组件：单个世界书条目

interface WorldBookEntryItemProps {
  entry: WorldBookEntry
  index: number
  onToggle: () => void
  onContentChange: (content: string) => void
}

function WorldBookEntryItem({
  entry,
  onToggle,
  onContentChange,
}: WorldBookEntryItemProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3">
        <div
          className="cursor-grab active:cursor-grabbing text-faint hover:text-fg transition-colors p-2 -m-2 touch-manipulation"
          style={{ touchAction: 'none' }}
        >
          <GripVertical size={18} />
        </div>

        <ToggleSwitch checked={entry.enabled} onChange={onToggle} />

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex-1 text-left flex items-center justify-between min-w-0 rounded-md px-2 py-1 -mx-2 hover:bg-raised/50 transition-colors"
        >
          <span
            className={`font-medium truncate ${
              entry.enabled ? 'text-fg' : 'text-faint line-through'
            }`}
          >
            {entry.comment || entry.name || `条目 ${entry.id}`}
          </span>
          {isExpanded ? (
            <ChevronUp size={16} className="text-faint shrink-0 ml-2" />
          ) : (
            <ChevronDown size={16} className="text-faint shrink-0 ml-2" />
          )}
        </button>
      </div>

      {/* Content Editor */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4">
              <div className="flex items-center gap-2 mb-2 text-xs text-faint">
                <AlertCircle size={12} className="text-faint" />
                <span>关键词: {entry.keys.join(', ') || '无'}</span>
                <span className="ml-auto px-1.5 py-0.5 rounded bg-raised text-muted">
                  {WORLD_INFO_POSITION_LABELS[entry.position as WorldInfoPositionValue] ?? `pos:${entry.position}`}
                </span>
                {entry.constant && (
                  <span className="px-1.5 py-0.5 rounded bg-accent-soft text-accent/90">constant</span>
                )}
              </div>
              <textarea
                value={entry.content}
                onChange={(e) => onContentChange(e.target.value)}
                rows={6}
                className="ui-textarea leading-relaxed min-h-[120px]"
                placeholder="条目内容..."
                style={{ touchAction: 'pan-y' }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
